import { find, each, reduce, snakeCase, cloneDeep } from 'lodash';

import { evalConditionsWithAppFields, isEmpty, objectToArray } from 'utils';

import { NAV_DEFAULTS, NAV_ICON_PREFIX } from '../constants/nav';

import { collectionLinkTarget } from './content';
import { translateAppLink } from './app';

export function navWithPreferences({ user, client, content }) {
  let navItems = cloneDeep(NAV_DEFAULTS);

  const userNav = cloneDeep(user?.preferences?.nav || client?.preferences?.nav);

  if (userNav?.length > 0) {
    const defaultNav = cloneDeep(NAV_DEFAULTS);

    // Add new items from defaults
    forEachNavItem(defaultNav, (navItem, index, parent, parentIndex) => {
      const userItem = findNavItem(navItem, userNav);
      if (!userItem) {
        if (parent) {
          const userItemParent = findNavItem(parent, userNav);
          if (userItemParent) {
            userItemParent.items = userItemParent.items || [];
            userItemParent.items.splice(index, 0, navItem);
          } else {
            // Append new item parent in the default location
            userNav.splice(parentIndex, 0, parent);
          }
        } else {
          userNav.splice(index, 0, navItem);
        }
      }
    });

    // Remove old default and disabled app items from user nav
    forEachNavItem(userNav, (navItem, index, parent) => {
      const defaultItem = findNavItem(navItem, defaultNav);
      // Custom items are ignored
      if (!defaultItem && !navItem?.custom) {
        if (parent) {
          parent.items.splice(index, 1);
        } else {
          userNav.splice(index, 1);
        }
      }
      if (navItem.app_id && !client.appsEnabledById[navItem.app_id]) {
        if (parent) {
          parent.items.splice(index, 1);
        } else {
          userNav.splice(index, 1);
        }
      }
    });

    navItems = userNav;
  }

  appendNavContentViews(navItems, content, client.appsEnabledById);

  return navItems;
}

/**
 * Apply the saved order and hidden state of the sidebar's Apps section.
 * Apps that aren't saved yet (installed since) go last.
 */
export function navAppsWithPreferences(appItems, savedApps) {
  if (!savedApps?.length) {
    return appItems;
  }

  const savedIndex = new Map(savedApps.map((app, index) => [app.id, index]));
  const getIndex = (item) => savedIndex.get(item.id) ?? savedApps.length;

  return appItems
    .map((item) =>
      savedApps[getIndex(item)]?.hidden ? { ...item, hidden: true } : item,
    )
    .sort((a, b) => getIndex(a) - getIndex(b));
}

export function forEachNavItem(
  navItems,
  callback,
  parent = undefined,
  parentIndex = undefined,
) {
  each(navItems, (item, index) => {
    const cont = callback(item, index, parent, parentIndex);
    if (cont === false) {
      return;
    }
    if (item?.items?.length) {
      forEachNavItem(item.items, callback, item, index);
    }
  });
}

export function findNavItem(item, navItems) {
  let foundItem;
  forEachNavItem(navItems, (navItem) => {
    if (navItem?.id === item?.id) {
      foundItem = navItem;
      return false;
    }
  });
  return foundItem;
}

export function findNavItemParent(item, navItems) {
  let foundIndex;
  let foundParent;
  forEachNavItem(navItems, (navItem, index, parent) => {
    if (navItem?.id === item?.id) {
      foundIndex = index;
      foundParent = parent;
      return false;
    }
  });
  return { foundIndex, foundParent };
}

export function appendNavContentViews(navItems, content, appsEnabledById) {
  if (
    Object.keys(content.modelsByApp).length === 0 &&
    Object.keys(content.viewsByCollection).length === 0
  ) {
    return;
  }

  const collectionSet = new Set();

  // Add app models nav
  const contentViews = Object.values(content.modelsByApp).reduce(
    (acc, models) => {
      for (const model of models) {
        if (model.nav) {
          collectionSet.add(model.collection);

          acc.push({
            nav: model.nav,
            app_id: model.app_id,
            collection: model.collection,
            date_created: model.collectionModel.date_created,
          });
        }
      }

      return acc;
    },
    [],
  );

  // Add app/custom content views
  Object.values(content.viewsByCollection).reduce((acc, views) => {
    for (const view of views) {
      if (
        view.type === 'list' &&
        view.nav &&
        !collectionSet.has(view.collection)
      ) {
        acc.push(view);
      }
    }

    return acc;
  }, contentViews);

  contentViews.sort((a, b) => {
    return (
      a.date_created ||
      a.collectionModel?.date_created ||
      '~'
    ).localeCompare(b.date_created || b.collectionModel?.date_created || '~');
  });

  // Append to nav items if not existing
  for (const view of contentViews) {
    const collection = view.childCollection || view.collection;

    const viewItem = {
      id: view.default ? view.collection : view.gid,
      label: view.nav?.label || view.label,
      link: view.nav?.link
        ? translateAppLink(view.nav, appsEnabledById[view.app_id])
        : collectionLinkTarget({ collection }),
      exact: true,
    };

    if (view.nav?.permission) {
      viewItem.permission = view.nav.permission;
    }

    if (view.nav?.icon) {
      viewItem.icon = `${NAV_ICON_PREFIX}${view.nav.icon}`;
    }

    if (view.nav?.target && view.nav.target !== 'self') {
      viewItem.target = '_' + view.nav.target;
    }

    if (view.app_id) {
      viewItem.app_id = view.app_id;
      viewItem.frontend = Boolean(view.nav?.link?.startsWith('frontend://'));

      if (
        view.nav?.link &&
        !view.nav.link.startsWith('frontend://') &&
        (!viewItem.target || viewItem.target === 'self')
      ) {
        viewItem.link += `?_external_link=${encodeURIComponent(view.nav.link)}`;
      }
    }

    const found = findNavItem(viewItem, navItems);

    if (view.active !== false) {
      if (!found) {
        if (view.nav?.parent) {
          const parent = find(navItems, { id: view.nav.parent });
          if (parent?.items) {
            parent.items.push(viewItem);
          }
        } else {
          navItems.push(viewItem);
        }
      }
    } else {
      // Remove inactive items
      if (found) {
        const { foundIndex, foundParent } = findNavItemParent(found, navItems);
        if (foundParent) {
          foundParent.items.splice(foundIndex, 1);
        } else if (foundIndex >= 0) {
          navItems.items.splice(foundIndex, 1);
        }
      }
    }
  }
}

/**
 * Get a view config with content views and preferences applied
 * (tabs, fields, actions, filters, etc)
 */
export function viewConfigsWithPreferences(
  configType,
  {
    currentView,
    user,
    client,
    content,
    defaults,
    overrides,
    conditionalValues,
  },
) {
  const userPrefs = user?.preferences;
  const clientPrefs = client?.preferences;
  const configsWithPreferences = [];

  // Start with default values (object format)
  const baseConfigs = [];
  const defaultConfigs = objectToArray(defaults);

  if (!currentView) {
    return defaultConfigs;
  }

  const collection = currentView.childCollection || currentView.collection;

  // Add content view values
  if (content) {
    // Standard views have other content view values applied
    if (currentView.standard) {
      const contentViews = content.viewsByCollection[collection]?.filter(
        (view) =>
          view.active !== false &&
          view.standard !== true &&
          view.type === currentView.type &&
          view.collection === collection,
      );
      for (const view of contentViews) {
        if (view.id !== currentView.id) {
          continue;
        }
        for (const viewConfig of view[configType] || []) {
          // Standard views use their own default things
          if (viewConfig.id === 'default' || viewConfig.default) {
            // Unless the standard view has no default, use this one
            if (currentView[configType]?.length === 0) {
              baseConfigs.push(viewConfig);
            }
            continue;
          }

          // Set config IDs on tab fields
          let { fields } = viewConfig;
          if (configType === 'tabs' && fields) {
            fields = fields.map((field) => ({
              ...field,
              id: `${view.app_id || 'admin'}_${field.id}`,
            }));
          }

          const configId = `${view.app_id || 'admin'}_${viewConfig.id}`;
          baseConfigs.push({
            ...viewConfig,
            id: configId,
            ...(fields ? { fields } : undefined),
            ...(view.app_id ? { app_id: view.app_id } : undefined),
          });
        }
      }
    } else {
      // Custom views use their own configs
      baseConfigs.push(...(currentView[configType] || []));
    }
  }

  // Add default configs to base configs
  const addConfigs = [];
  for (const config of defaultConfigs) {
    if (!baseConfigs.some((item) => item.id === config.id)) {
      addConfigs.push(config);
    }
  }
  // Make sure default configs are added in the right order
  baseConfigs.unshift(...addConfigs);

  // Apply config preferences
  let prefView = overrides || find(userPrefs?.views, { id: currentView.gid });
  if (!prefView?.[configType]) {
    prefView = find(clientPrefs?.views, { id: currentView.gid });
  }

  for (const prefConfig of prefView?.[configType] || []) {
    if (!prefConfig) {
      continue;
    }
    if (prefConfig.app_id && !client.appsEnabledById[prefConfig.app_id]) {
      continue;
    }

    const configId = prefConfig.id || `admin_${snakeCase(prefConfig.label)}`;
    const base =
      find(baseConfigs, { id: configId }) ||
      find(baseConfigs, { id: prefConfig.source_id });

    if (!base && !prefConfig.custom) {
      continue;
    }

    const isHidden =
      prefConfig.hidden || (!overrides && base.hidden && !base.fieldFromModel);
    const configWithPref = {
      ...(base || undefined),
      ...prefConfig,
      ...(isHidden ? { hidden: true } : undefined),
      id: configId,
    };

    // Set hidden if not overriding fields, otherwise remove it from base def
    if (isHidden) {
      configWithPref.hidden = true;
    } else {
      delete configWithPref.hidden;
    }

    configsWithPreferences.push(configWithPref);
  }

  // Add all base configs not in preferences
  for (const config of baseConfigs) {
    if (!configsWithPreferences.some((item) => item.id === config.id)) {
      configsWithPreferences.push(config);
    }
  }

  // Add hidden flags to conditional fields
  return configsWithPreferences.map((config) => {
    if (config.id === 'default' || config.default) {
      // Ensure default configs are not hidden
      delete config.hidden;
      config.default = true;
    }
    if (
      !matchPreferenceConditions(currentView, config, client, conditionalValues)
    ) {
      config.hidden = true;
      config.hiddenByConditions = true;
    }
    return config;
  });
}

function matchPreferenceConditions(
  currentView,
  config,
  client,
  conditionalValues,
) {
  if (!isEmpty(config.conditions)) {
    const app = client?.appsById[currentView.app_id];
    const rootPath = conditionalValues && app ? `$app.${app.slug_id}` : '';

    // Remove field conditions if the view is a list type
    const viewConditions =
      currentView.type === 'record'
        ? config.conditions
        : reduce(
            config.conditions,
            (acc, value, key) => {
              if (key === '$settings' || key.startsWith('$settings.')) {
                acc[key] = value;
              }

              return acc;
            },
            {},
          );

    if (Object.keys(viewConditions).length === 0) {
      return true;
    }

    const result = evalConditionsWithAppFields(
      client,
      rootPath,
      viewConditions,
      conditionalValues,
    );

    return result;
  }
  return true;
}
